import React, { useState, useEffect } from 'react';
import supabase from '../../supabase/supabase';
import { FiUser, FiMail, FiPhone, FiMapPin, FiCalendar, FiActivity, FiX, FiTrash2, FiShoppingBag, FiSearch, FiUsers, FiUserX, FiShield } from 'react-icons/fi';
import { useStore } from '../../context/StoreContext';
import SafeIcon from '../../common/SafeIcon';

const AdminCustomers = () => {
  const { user } = useStore();
  const [customers, setCustomers] = useState([]);
  const [guests, setGuests] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerOrders, setCustomerOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('customers');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    await Promise.all([fetchCustomers(), fetchGuests()]);
    setLoading(false);
  };

  const fetchCustomers = async () => {
    const { data: profiles, error } = await supabase
      .from('profiles')
      .select('*, orders(id)')
      .order('created_at', { ascending: false });

    if (error) console.error("Error fetching customers:", error);
    
    if (profiles) {
      const formatted = profiles.map(p => ({
        ...p,
        totalOrders: p.orders?.length || 0
      }));

      // Separate admins from regular customers
      const adminList = formatted.filter(p => p.role === 'admin');
      const customerList = formatted.filter(p => p.role !== 'admin');

      setAdmins(adminList);
      setCustomers(customerList);
    }
  };

  const fetchGuests = async () => {
    const { data: orders, error } = await supabase
      .from('orders')
      .select('id, shipping_address, total_amount, status, payment_status, created_at, user_id')
      .order('created_at', { ascending: false });

    if (error) {
      console.error("Error fetching guest orders:", error);
      return;
    }

    if (orders) {
      // Get all registered customer emails
      const { data: allProfiles } = await supabase.from('profiles').select('email');
      const registeredEmails = new Set(
        (allProfiles || []).map(p => p.email?.toLowerCase()).filter(Boolean)
      );

      // Group guest orders by email (only orders with no user_id)
      const guestMap = {};
      orders.forEach(order => {
        // Skip orders that belong to registered users
        if (order.user_id) return;

        const email = order.shipping_address?.email?.toLowerCase();
        if (!email) return;

        // Skip if this guest email matches a registered customer
        if (registeredEmails.has(email)) return;

        if (!guestMap[email]) {
          guestMap[email] = {
            email,
            firstName: order.shipping_address?.firstName || '',
            lastName: order.shipping_address?.lastName || '',
            phone: order.shipping_address?.phone || '',
            countryCode: order.shipping_address?.countryCode || '',
            address: order.shipping_address?.addressLine1 || order.shipping_address?.address || '',
            city: order.shipping_address?.city || '',
            orders: [],
            totalSpent: 0,
            firstOrder: order.created_at
          };
        }

        guestMap[email].orders.push(order);
        guestMap[email].totalSpent += Number(order.total_amount) || 0;
        if (new Date(order.created_at) < new Date(guestMap[email].firstOrder)) {
          guestMap[email].firstOrder = order.created_at;
        }
      });

      setGuests(Object.values(guestMap).sort((a, b) => new Date(b.firstOrder) - new Date(a.firstOrder)));
    }
  };

  const handleViewProfile = async (customer) => {
    setSelectedCustomer(customer);
    setLoadingOrders(true);
    const { data, error } = await supabase
      .from('orders')
      .select('*')
      .eq('user_id', customer.id)
      .order('created_at', { ascending: false });

    if (data) setCustomerOrders(data);
    setLoadingOrders(false);
  };

  const handleViewGuest = (guest) => {
    setSelectedCustomer({ ...guest, isGuest: true });
    setCustomerOrders(guest.orders);
  };

  const handleViewAdmin = async (admin) => {
    setSelectedCustomer(admin);
    setLoadingOrders(true);
    const { data, error } = await supabase
      .from('orders')
      .select('*')
      .eq('user_id', admin.id)
      .order('created_at', { ascending: false });

    if (data) setCustomerOrders(data);
    setLoadingOrders(false);
  };

  const handleDeleteCustomer = async (id) => {
    if (window.confirm('Are you sure you want to completely delete this user? This will remove their account and login access entirely.')) {
      const { error } = await supabase.rpc('delete_user_admin', { target_user_id: id });
      
      if (error) {
        alert("Failed to delete customer: " + error.message);
      } else {
        fetchCustomers();
      }
    }
  };

  const filteredCustomers = customers.filter(customer => {
    const term = searchTerm.toLowerCase();
    const name = customer.full_name?.toLowerCase() || '';
    const email = customer.email?.toLowerCase() || '';
    return name.includes(term) || email.includes(term);
  });

  const filteredGuests = guests.filter(guest => {
    const term = searchTerm.toLowerCase();
    const name = `${guest.firstName} ${guest.lastName}`.toLowerCase();
    const email = guest.email?.toLowerCase() || '';
    return name.includes(term) || email.includes(term);
  });

  const filteredAdmins = admins.filter(admin => {
    const term = searchTerm.toLowerCase();
    const name = admin.full_name?.toLowerCase() || '';
    const email = admin.email?.toLowerCase() || '';
    return name.includes(term) || email.includes(term);
  });

  if (loading) return <div className="p-8 text-center text-gray-500">Loading...</div>;

  const tabConfig = [
    { key: 'customers', label: 'Customers', icon: FiUsers, count: customers.length },
    { key: 'guests', label: 'Guests', icon: FiUserX, count: guests.length },
    { key: 'admins', label: 'Admins', icon: FiShield, count: admins.length },
  ];

  return (
    <div className="space-y-6">
      {/* Tab Toggle */}
      <div className="flex items-center gap-2 bg-white rounded-2xl p-1.5 border border-gray-100 shadow-sm w-fit">
        {tabConfig.map(tab => (
          <button
            key={tab.key}
            onClick={() => { setActiveTab(tab.key); setSearchTerm(''); }}
            className={`px-5 py-2.5 rounded-xl text-xs font-bold uppercase tracking-widest transition-all flex items-center gap-2 ${
              activeTab === tab.key
                ? 'bg-brand-darkSage text-white shadow-md'
                : 'text-gray-500 hover:text-brand-darkSage'
            }`}
          >
            <SafeIcon icon={tab.icon} size={14} />
            {tab.label}
            <span className={`px-2 py-0.5 rounded-full text-[10px] ${
              activeTab === tab.key ? 'bg-white/20' : 'bg-gray-100'
            }`}>
              {tab.count}
            </span>
          </button>
        ))}
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="p-6 border-b border-gray-100 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <h2 className="text-xl font-serif text-gray-800">
            {activeTab === 'customers' ? 'Registered Customers' : 
             activeTab === 'guests' ? 'Guest Checkout Customers' : 'Admin Accounts'}
          </h2>
          <div className="relative w-full sm:w-72">
            <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder={`Search ${activeTab}...`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-4 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:border-brand-gold focus:ring-1 focus:ring-brand-gold transition-all bg-gray-50/50"
            />
          </div>
        </div>
        
        {/* Customers Table */}
        {activeTab === 'customers' && (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-gray-50/50 text-gray-500 text-xs uppercase tracking-wider">
                  <th className="p-4 font-medium">Customer</th>
                  <th className="p-4 font-medium">Email</th>
                  <th className="p-4 font-medium">Gender</th>
                  <th className="p-4 font-medium">Age</th>
                  <th className="p-4 font-medium">Contact</th>
                  <th className="p-4 font-medium">Orders</th>
                  <th className="p-4 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredCustomers.map(customer => (
                  <tr key={customer.id} className="hover:bg-gray-50/50 transition-colors">
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-brand-gold/10 flex items-center justify-center text-brand-gold font-bold">
                          {customer.full_name?.charAt(0) || 'U'}
                        </div>
                        <span className="font-semibold text-gray-800">{customer.full_name || 'Unnamed'}</span>
                      </div>
                    </td>
                    <td className="p-4 text-sm text-gray-600 truncate max-w-[150px]" title={customer.email || 'N/A'}>{customer.email || 'N/A'}</td>
                    <td className="p-4 text-sm text-gray-600">{customer.gender || 'N/A'}</td>
                    <td className="p-4 text-sm text-gray-600">{customer.age || 'N/A'}</td>
                    <td className="p-4 text-sm text-gray-600">{customer.phone_number || 'N/A'}</td>
                    <td className="p-4">
                      <span className="bg-brand-sand px-2 py-1 rounded-lg text-xs font-bold text-brand-darkSage">
                        {customer.totalOrders}
                      </span>
                    </td>
                    <td className="p-4 text-right space-x-4">
                      <button 
                        onClick={() => handleViewProfile(customer)}
                        className="text-brand-gold hover:text-brand-darkSage font-bold text-xs uppercase tracking-widest transition-colors"
                      >
                        View
                      </button>
                      {user?.id !== customer.id && (
                        <button 
                          onClick={() => handleDeleteCustomer(customer.id)}
                          className="text-red-400 hover:text-red-600 transition-colors"
                          title="Delete Customer"
                        >
                          <FiTrash2 size={16} className="inline" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
                {filteredCustomers.length === 0 && (
                  <tr>
                    <td colSpan="7" className="p-8 text-center text-gray-500 text-sm">
                      {searchTerm ? 'No customers match your search.' : 'No registered customers found.'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Guests Table */}
        {activeTab === 'guests' && (
          <div className="overflow-x-auto">
            {filteredGuests.length === 0 ? (
              <div className="p-12 text-center">
                <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-4">
                  <SafeIcon icon={FiUserX} className="w-8 h-8 text-gray-300" />
                </div>
                <p className="text-gray-400 text-sm">
                  {searchTerm ? 'No guests match your search.' : 'No guest checkouts yet.'}
                </p>
                <p className="text-gray-300 text-xs mt-1">Guests who create an account will automatically move to the Customers tab.</p>
              </div>
            ) : (
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-gray-50/50 text-gray-500 text-xs uppercase tracking-wider">
                    <th className="p-4 font-medium">Guest</th>
                    <th className="p-4 font-medium">Email</th>
                    <th className="p-4 font-medium">Phone</th>
                    <th className="p-4 font-medium">Location</th>
                    <th className="p-4 font-medium">Orders</th>
                    <th className="p-4 font-medium">Total Spent</th>
                    <th className="p-4 font-medium text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredGuests.map(guest => (
                    <tr key={guest.email} className="hover:bg-gray-50/50 transition-colors">
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center text-orange-600 font-bold">
                            {guest.firstName?.charAt(0) || 'G'}
                          </div>
                          <div className="flex flex-col">
                            <span className="font-semibold text-gray-800">{guest.firstName} {guest.lastName}</span>
                            <span className="text-[10px] text-orange-500 font-bold uppercase tracking-widest leading-none mt-1">Guest</span>
                          </div>
                        </div>
                      </td>
                      <td className="p-4 text-sm text-gray-600 truncate max-w-[180px]" title={guest.email}>{guest.email}</td>
                      <td className="p-4 text-sm text-gray-600">{guest.countryCode} {guest.phone}</td>
                      <td className="p-4 text-sm text-gray-600 truncate max-w-[120px]">{guest.city}</td>
                      <td className="p-4">
                        <span className="bg-orange-50 px-2 py-1 rounded-lg text-xs font-bold text-orange-600">
                          {guest.orders.length}
                        </span>
                      </td>
                      <td className="p-4 text-sm font-bold text-gray-800">Rs. {guest.totalSpent.toFixed(2)}</td>
                      <td className="p-4 text-right">
                        <button 
                          onClick={() => handleViewGuest(guest)}
                          className="text-brand-gold hover:text-brand-darkSage font-bold text-xs uppercase tracking-widest transition-colors"
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* Admins Table */}
        {activeTab === 'admins' && (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-gray-50/50 text-gray-500 text-xs uppercase tracking-wider">
                  <th className="p-4 font-medium">Admin</th>
                  <th className="p-4 font-medium">Email</th>
                  <th className="p-4 font-medium">Contact</th>
                  <th className="p-4 font-medium">Joined</th>
                  <th className="p-4 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredAdmins.map(admin => (
                  <tr key={admin.id} className="hover:bg-gray-50/50 transition-colors">
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-brand-gold/20 flex items-center justify-center text-brand-gold font-bold">
                          {admin.full_name?.charAt(0) || 'A'}
                        </div>
                        <div className="flex flex-col">
                          <span className="font-semibold text-gray-800">{admin.full_name || 'Admin'}</span>
                          <span className="text-[10px] text-brand-gold font-bold uppercase tracking-widest leading-none mt-1">Administrator</span>
                        </div>
                      </div>
                    </td>
                    <td className="p-4 text-sm text-gray-600 truncate max-w-[200px]" title={admin.email || 'N/A'}>{admin.email || 'N/A'}</td>
                    <td className="p-4 text-sm text-gray-600">{admin.phone_number || 'N/A'}</td>
                    <td className="p-4 text-sm text-gray-600">
                      {admin.created_at ? new Date(admin.created_at).toLocaleDateString() : 'N/A'}
                    </td>
                    <td className="p-4 text-right">
                      <button 
                        onClick={() => handleViewAdmin(admin)}
                        className="text-brand-gold hover:text-brand-darkSage font-bold text-xs uppercase tracking-widest transition-colors"
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))}
                {filteredAdmins.length === 0 && (
                  <tr>
                    <td colSpan="5" className="p-8 text-center text-gray-500 text-sm">
                      {searchTerm ? 'No admins match your search.' : 'No admin accounts found.'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Customer/Guest/Admin Detail Modal */}
      {selectedCustomer && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[200] flex items-center justify-center p-4">
          <div className="bg-white rounded-[2rem] w-full max-w-lg overflow-hidden shadow-2xl relative max-h-[90vh] flex flex-col">
            <button 
              onClick={() => setSelectedCustomer(null)}
              className="absolute top-6 right-6 p-2 hover:bg-gray-100 rounded-full transition-colors z-10"
            >
              <FiX className="w-6 h-6 text-gray-400" />
            </button>

            <div className="p-8 md:p-10 overflow-y-auto custom-scrollbar">
              <div className="flex flex-col items-center text-center mb-8">
                <div className={`w-20 h-20 rounded-full flex items-center justify-center text-3xl font-serif font-bold mb-4 ${
                  selectedCustomer.isGuest ? 'bg-orange-100 text-orange-600' : 
                  selectedCustomer.role === 'admin' ? 'bg-brand-gold/20 text-brand-gold' :
                  'bg-brand-gold/10 text-brand-gold'
                }`}>
                  {selectedCustomer.isGuest 
                    ? (selectedCustomer.firstName?.charAt(0) || 'G')
                    : (selectedCustomer.full_name?.charAt(0) || 'U')
                  }
                </div>
                <h3 className="text-2xl font-serif font-bold text-gray-800">
                  {selectedCustomer.isGuest 
                    ? `${selectedCustomer.firstName} ${selectedCustomer.lastName}`
                    : selectedCustomer.full_name
                  }
                  {selectedCustomer.role === 'admin' && <span className="ml-2 text-xs bg-brand-gold text-white px-2 py-1 rounded-full uppercase tracking-widest font-sans align-middle">Admin</span>}
                  {selectedCustomer.isGuest && <span className="ml-2 text-xs bg-orange-500 text-white px-2 py-1 rounded-full uppercase tracking-widest font-sans align-middle">Guest</span>}
                </h3>
                <p className="text-brand-gold font-semibold uppercase tracking-widest text-[10px] mt-1">
                  {selectedCustomer.isGuest 
                    ? `First Order ${new Date(selectedCustomer.firstOrder).toLocaleDateString()}`
                    : `Member Since ${new Date(selectedCustomer.created_at).toLocaleDateString()}`
                  }
                </p>
              </div>

              <div className="space-y-6">
                <div className="grid grid-cols-2 gap-4 bg-gray-50/50 p-5 rounded-2xl border border-gray-100">
                  <DetailItem icon={FiMail} label="Email" value={selectedCustomer.isGuest ? selectedCustomer.email : (selectedCustomer.email || 'N/A')} />
                  <DetailItem icon={FiPhone} label="Phone" value={selectedCustomer.isGuest ? `${selectedCustomer.countryCode} ${selectedCustomer.phone}` : (selectedCustomer.phone_number || 'N/A')} />
                  {!selectedCustomer.isGuest && (
                    <>
                      <DetailItem icon={FiActivity} label="Gender" value={selectedCustomer.gender || 'N/A'} />
                      <DetailItem icon={FiCalendar} label="Age" value={selectedCustomer.age || 'N/A'} />
                    </>
                  )}
                  <DetailItem icon={FiMapPin} label="Address" value={selectedCustomer.isGuest ? `${selectedCustomer.address}, ${selectedCustomer.city}` : (selectedCustomer.address || 'N/A')} fullWidth />
                </div>

                {/* Orders Section */}
                <div className="pt-2">
                  <div className="flex justify-between items-center mb-4">
                    <h4 className="text-sm font-bold text-gray-800 flex items-center gap-2">
                      <FiShoppingBag className="text-brand-gold" />
                      Order History
                    </h4>
                    <span className="text-xs font-bold text-brand-darkSage bg-brand-sand px-2 py-1 rounded-lg">
                      {selectedCustomer.isGuest ? selectedCustomer.orders.length : selectedCustomer.totalOrders} Total
                    </span>
                  </div>

                  {loadingOrders && !selectedCustomer.isGuest ? (
                    <div className="text-xs text-gray-500 text-center py-6 bg-gray-50 rounded-xl">Loading orders...</div>
                  ) : customerOrders.length === 0 ? (
                    <div className="text-xs text-gray-500 text-center py-6 bg-gray-50 rounded-xl border border-gray-100">No orders placed yet.</div>
                  ) : (
                    <div className="space-y-3">
                      {customerOrders.map(order => (
                        <div key={order.id} className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm flex justify-between items-center transition-all hover:border-brand-gold/30">
                          <div>
                            <p className="text-xs font-mono text-gray-400 mb-1">#{order.id.split('-')[0]}</p>
                            <p className="text-sm font-semibold text-gray-800">{new Date(order.created_at).toLocaleDateString()}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-bold text-brand-darkSage mb-1">Rs. {order.total_amount.toFixed(2)}</p>
                            <span className="text-[10px] font-bold uppercase tracking-widest text-brand-gold">
                              {order.status}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const DetailItem = ({ icon: Icon, label, value, fullWidth = false }) => (
  <div className={fullWidth ? "col-span-2" : ""}>
    <div className="flex items-center gap-2 text-gray-400 mb-1">
      <SafeIcon icon={Icon} className="w-3.5 h-3.5" />
      <span className="text-[10px] font-bold uppercase tracking-widest">{label}</span>
    </div>
    <p className="text-sm font-medium text-gray-800 truncate" title={value}>{value}</p>
  </div>
);

export default AdminCustomers;